import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../api";
import { useAuth } from "../AuthContext";
import { useI18n } from "../I18nContext";
import RecurrenceFields, { appendRecurrence, EMPTY_RECURRENCE } from "./RecurrenceFields";

const MEETING_ROLES = ["advisor", "accelerator", "entrepreneur", "investor", "provider"];
const ROLE_LABEL = {
  entrepreneur: "founder",
  advisor: "advisor",
  accelerator: "accelerator",
  provider: "provider",
  investor: "fundManager",
};

function bookingAudience(user, counterpart, isHost) {
  if (isHost) return "advisor";
  const roles = new Set([user?.role, counterpart?.role].filter(Boolean));
  if (roles.has("entrepreneur")) {
    for (const aud of ["advisor", "accelerator", "investor", "provider"]) {
      if (roles.has(aud)) return aud;
    }
  }
  if (["advisor", "accelerator", "investor", "provider"].includes(counterpart?.role)) return counterpart.role;
  return "advisor";
}

function sortPeople(list) {
  return [...list].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
}

function personOption(p, t) {
  const role = t(`roles.${ROLE_LABEL[p.role] || "members"}`);
  return `${p.name}${p.company ? ` · ${p.company}` : ""} · ${role}`;
}

function bookingPrefill(params) {
  const freq = params.get("freq") || "";
  const start = params.get("start") || "";
  const duration = Number(params.get("duration"));
  const rate = params.get("rate") || "";
  return {
    counterpart_id: params.get("with") || "",
    duration_min: duration >= 15 && duration <= 180 ? duration : 60,
    title: params.get("title") || "",
    scheduled_at: /^\d{4}-\d{2}-\d{2}$/.test(start) ? `${start}T09:00` : defaultWhenLocal(),
    rate_amount: rate,
    rate_currency: params.get("currency") || "CAD",
    recurrence: freq === "monthly" ? "monthly" : freq === "weekly" || freq === "biweekly" ? "weekly" : "",
    recurrence_interval: freq === "biweekly" ? 2 : 1,
  };
}

function defaultWhenLocal() {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  const tz = d.getTimezoneOffset();
  return new Date(d.getTime() - tz * 60000).toISOString().slice(0, 16);
}

export default function NewSession() {
  const { t } = useI18n();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefill = bookingPrefill(params);
  const isHost = user.role === "admin";
  const [people, setPeople] = useState([]);
  const [founders, setFounders] = useState([]);
  const [advisors, setAdvisors] = useState([]);
  const [colleagues, setColleagues] = useState([]);
  const [others, setOthers] = useState([]);
  const [form, setForm] = useState({
    counterpart_id: prefill.counterpart_id,
    advisor_id: "",
    entrepreneur_id: "",
    colleague_id: "",
    other_id: "",
    scheduled_at: prefill.scheduled_at,
    duration_min: prefill.duration_min,
    title: prefill.title,
    meeting_link: "",
    observer_ids: [],
    rate_amount: prefill.rate_amount,
    rate_currency: prefill.rate_currency,
    template_id: "",
    ...EMPTY_RECURRENCE,
    recurrence: prefill.recurrence,
    recurrence_interval: prefill.recurrence_interval,
  });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (isHost) {
      if (user.role === "admin") {
        Promise.all([
          api.get("/users/entrepreneurs"),
          api.get("/users/advisors"),
        ]).then(([foundersRes, advisorsRes]) => {
          setFounders(sortPeople((foundersRes.data || []).map((u) => ({ ...u, role: "entrepreneur" }))));
          setAdvisors(sortPeople((advisorsRes.data || []).map((u) => ({ ...u, role: "advisor" }))));
        }).catch(() => {
          setFounders([]);
          setAdvisors([]);
        });
        api.get("/users/observers").then((r) => {
          const list = r.data || [];
          setColleagues(sortPeople(list.filter((u) => u.role === "accelerator" && u.id !== user.id)));
          setOthers(sortPeople(list.filter((u) => u.role === "provider" || u.role === "investor")));
        }).catch(() => {
          setColleagues([]);
          setOthers([]);
        });
      } else {
        api.get("/community").then((r) => {
          const mine = r.data?.mine || {};
          setFounders(sortPeople(mine.entrepreneur || []));
          setAdvisors(sortPeople(mine.advisor || []));
          setColleagues(sortPeople((mine.accelerator || []).filter((u) => u.id !== user.id)));
          setOthers(sortPeople([...(mine.provider || []), ...(mine.investor || [])]));
        }).catch(() => {
          setFounders([]);
          setAdvisors([]);
          setColleagues([]);
          setOthers([]);
        });
      }
    } else {
      api.get("/community").then((r) => {
        const mine = r.data?.mine || {};
        const seen = new Set();
        const next = [];
        MEETING_ROLES.forEach((role) => {
          (mine[role] || []).forEach((u) => {
            if (!u?.id || u.id === user.id || seen.has(u.id)) return;
            seen.add(u.id);
            next.push(u);
          });
        });
        setPeople(sortPeople(next));
      }).catch(() => setPeople([]));
    }
    api.get("/meeting-templates?session_type=advisory").then((r) => {
      const list = r.data || [];
      setTemplates(list);
    }).catch(() => setTemplates([]));
  }, [user.role, user.id, isHost]);

  const counterpart = people.find((p) => p.id === form.counterpart_id);
  const aud = bookingAudience(user, counterpart, isHost);
  const shownTemplates = templates
    .filter((tpl) => (tpl.audience || "advisor") === aud)
    .slice()
    .sort((a, b) => Number(!!b.is_default) - Number(!!a.is_default));
  useEffect(() => {
    const list = templates.filter((tpl) => (tpl.audience || "advisor") === aud);
    const def = list.find((x) => x.is_default) || list[0];
    if (!def) return;
    setForm((f) => (f.template_id === def.id ? f : { ...f, template_id: def.id }));
  }, [aud, templates]);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const payload = {
        scheduled_at: new Date(form.scheduled_at).toISOString(),
        duration_min: Number(form.duration_min) || 60,
        title: form.title,
        meeting_link: form.meeting_link,
        observer_ids: [],
        rate_cents: Math.round((Number(form.rate_amount) || 0) * 100),
        rate_currency: form.rate_currency || "CAD",
      };
      if (isHost) {
        payload.advisor_id = form.advisor_id;
        payload.entrepreneur_id = form.entrepreneur_id;
        if (form.colleague_id) payload.colleague_id = form.colleague_id;
        if (form.other_id) payload.other_id = form.other_id;
      } else {
        payload.counterpart_id = form.counterpart_id;
      }
      if (form.template_id) payload.template_id = form.template_id;
      if (!payload.title) {
        const tpl = shownTemplates.find((x) => x.id === form.template_id);
        if (tpl?.name) payload.title = tpl.name;
      }
      appendRecurrence(payload, form);
      const { data } = await api.post("/sessions", payload);
      navigate(`/app/sessions/${data.id}`);
    } catch (e2) {
      setErr(formatApiErrorDetail(e2.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  const cannotSubmit = isHost
    ? loading || founders.length === 0 || advisors.length === 0
    : loading || people.length === 0;

  return (
    <div className="tb-page">
      <h1 className="tb-h1" data-testid="new-session-heading">
        {t("book.title")}
      </h1>
      <form className="tb-form" onSubmit={submit} data-testid="new-session-form">
        {isHost ? (
          <>
          <div className="tb-row">
            <div>
              <label>{t("roles.founder")}</label>
              <select
                required={founders.length > 0}
                value={form.entrepreneur_id}
                onChange={onChange("entrepreneur_id")}
                data-testid="select-founder"
                disabled={founders.length === 0}
              >
                <option value="">{founders.length === 0 ? t("book.noFounders") : t("book.pickFounder")}</option>
                {founders.map((p) => (
                  <option key={p.id} value={p.id}>{personOption(p, t)}</option>
                ))}
              </select>
            </div>
            <div>
              <label>{t("roles.advisor")}</label>
              <select
                required={advisors.length > 0}
                value={form.advisor_id}
                onChange={onChange("advisor_id")}
                data-testid="select-advisor"
                disabled={advisors.length === 0}
              >
                <option value="">{advisors.length === 0 ? t("book.noAdvisors") : t("book.pickAdvisor")}</option>
                {advisors.map((p) => (
                  <option key={p.id} value={p.id}>{personOption(p, t)}</option>
                ))}
              </select>
            </div>
          </div>
          </>
        ) : (
          <div>
            <label>{t("book.with")}</label>
            <select required={people.length > 0} value={form.counterpart_id} onChange={onChange("counterpart_id")} data-testid="select-counterpart" disabled={people.length === 0}>
              <option value="">{people.length === 0 ? t("book.noPeople") : t("book.pickPerson")}</option>
              {people.map((p) => (
                <option key={p.id} value={p.id}>{personOption(p, t)}</option>
              ))}
            </select>
          </div>
        )}

        <div className="tb-row">
          <div>
            <label>{t("book.when")}</label>
            <input type="datetime-local" required value={form.scheduled_at} onChange={onChange("scheduled_at")} data-testid="input-scheduled-at" />
          </div>
          <div>
            <label>{t("book.duration")}</label>
            <input type="number" min="15" max="180" step="15" value={form.duration_min} onChange={onChange("duration_min")} data-testid="input-duration" />
          </div>
        </div>
        <RecurrenceFields form={form} setForm={setForm} />
        <div>
          <label>{t("book.sessionTitle")}</label>
          <input type="text" value={form.title} onChange={onChange("title")} data-testid="input-title" placeholder="Monthly advisory — Q2" />
        </div>
        {shownTemplates.length > 0 && (
          <div>
            <label>{t("book.template")}</label>
            <select value={form.template_id} onChange={onChange("template_id")} data-testid="select-advisory-template">
              {shownTemplates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}{tpl.is_default ? ` · ${t("settings.templates.default")}` : ""}
                </option>
              ))}
            </select>
            {(user.role === "advisor" || user.role === "admin" || user.role === "accelerator" || user.role === "investor" || user.role === "provider" || (user.role === "entrepreneur" && !user.team_owner_id)) && (
              <Link to="/app/settings/templates?type=advisory" style={{ display: "inline-block", marginTop: 6, fontSize: "0.85rem" }} data-testid="manage-advisory-templates">
                {t("team1v1.manageTemplates")} →
              </Link>
            )}
          </div>
        )}
        <div>
          <label>{t("book.meetingLink")}</label>
          <input type="url" value={form.meeting_link} onChange={onChange("meeting_link")} data-testid="input-meeting-link" placeholder="https://meet.google.com/... · zoom.us/j/... · teams.microsoft.com/..." />
          <small className="tb-muted" style={{ display: "block", marginTop: 4, fontSize: "0.78rem" }}>
            {t("book.meetingLinkHelp")}
          </small>
        </div>
        {err && <div className="tb-form-error" data-testid="new-session-error">{err}</div>}
        <div style={{ display: "flex", gap: 8 }}>
          <button type="button" className="tb-ghost-btn" onClick={() => navigate("/app/bookings")} data-testid="btn-cancel">
            {t("book.cancel")}
          </button>
          <button type="submit" className="tb-primary-btn" disabled={cannotSubmit} data-testid="btn-create-session">
            {loading ? t("auth.loading") : t("book.create")}
          </button>
        </div>
      </form>
    </div>
  );
}
